import React, { useState } from 'react';
import axios from 'axios';

const DonatePage = ({ language }) => {
  const [amount, setAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const quickAmounts = [100, 500, 1000, 5000, 10000];

  const handleDonate = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1) {
      alert('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const orderRes = await axios.post(`${API}/payments/razorpay/create-order`, {
        amount: parseInt(amount),
        currency: 'INR',
        receipt: `donation_${Date.now()}`,
        notes: { donor: donorInfo.name, type: 'donation' }
      });

      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: 'INR',
        name: 'Ahobilam.com',
        description: 'Donation for Religious Services',
        order_id: orderRes.data.order_id,
        handler: function (response) {
          alert(language === 'en' 
            ? 'Thank you for your generous donation! 🙏' 
            : 'உங்கள் தாராள நன்கொடைக்கு நன்றி! 🙏');
          setAmount('');
          setDonorInfo({ name: '', email: '', phone: '' });
        },
        prefill: {
          name: donorInfo.name,
          email: donorInfo.email,
          contact: donorInfo.phone
        },
        theme: { color: '#16a34a' }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Donation error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 to-green-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-green-800 mb-4">
              {language === 'en' ? '🙏 Support Our Mission' : '🙏 எங்கள் பணியை ஆதரியுங்கள்'}
            </h1>
            <p className="text-lg text-gray-700">
              {language === 'en'
                ? 'Your generous donation helps us provide free religious services to the community'
                : 'உங்கள் தாராள நன்கொடை சமூகத்திற்கு இலவச மத சேவைகளை வழங்க எங்களுக்கு உதவுகிறது'}
            </p>
          </div>

          <form onSubmit={handleDonate} className="space-y-6">
            <div>
              <label className="block text-lg font-semibold mb-3">
                {language === 'en' ? 'Select or Enter Amount' : 'தொகையைத் தேர்ந்தெடுக்கவும் அல்லது உள்ளிடவும்'}
              </label>
              <div className="grid grid-cols-5 gap-3 mb-4">
                {quickAmounts.map(amt => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAmount(amt.toString())}
                    className={`py-3 rounded-lg font-bold transition ${
                      amount === amt.toString()
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-4 top-3 text-2xl text-gray-600">₹</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter custom amount"
                  className="w-full pl-12 pr-4 py-3 text-xl border-2 rounded-lg focus:ring-2 focus:ring-green-500"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                placeholder={language === 'en' ? 'Your Name' : 'உங்கள் பெயர்'}
                value={donorInfo.name}
                onChange={(e) => setDonorInfo({...donorInfo, name: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                required
              />
              <input
                type="email"
                placeholder={language === 'en' ? 'Email' : 'மின்னஞ்சல்'}
                value={donorInfo.email}
                onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                required
              />
              <input
                type="tel"
                placeholder={language === 'en' ? 'Phone Number' : 'தொலைபேசி எண்'}
                value={donorInfo.phone}
                onChange={(e) => setDonorInfo({...donorInfo, phone: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-600 to-green-600 text-white py-4 rounded-lg hover:from-orange-700 hover:to-green-700 font-bold text-xl disabled:opacity-50"
            >
              {loading ? 'Processing...' : (language === 'en' ? 'Donate via Razorpay 🙏' : 'Razorpay மூலம் நன்கொடை 🙏')}
            </button>
          </form>

          {/* UPI/BHIM Payment Option */}
          <div className="mt-8 border-t-2 border-gray-200 pt-8">
            <h3 className="text-2xl font-bold text-center text-green-800 mb-6">
              {language === 'en' ? '💳 Pay via UPI/BHIM' : '💳 UPI/BHIM மூலம் செலுத்துங்கள்'}
            </h3>
            <div className="bg-gradient-to-br from-orange-50 to-green-50 rounded-xl p-6 text-center">
              <img 
                src="/bhim-qr.jpg" 
                alt="UPI QR Code" 
                className="mx-auto max-w-xs w-full rounded-lg shadow-lg mb-4"
              />
              <p className="text-lg font-semibold text-gray-800 mb-2">
                {language === 'en' ? 'Scan QR Code with any UPI App' : 'எந்த UPI செயலி மூலமும் QR குறியீட்டை ஸ்கேன் செய்யவும்'}
              </p>
              <p className="text-gray-600 mb-4">
                {language === 'en' ? 'PhonePe • Google Pay • Paytm • BHIM' : 'PhonePe • Google Pay • Paytm • BHIM'}
              </p>
              <div className="bg-white rounded-lg p-4 inline-block">
                <p className="text-sm text-gray-600 mb-1">
                  {language === 'en' ? 'UPI ID:' : 'UPI ID:'}
                </p>
                <p className="text-xl font-mono font-bold text-green-700">svknvs@okicici</p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-600">
            <p>{language === 'en' ? 'Secure payment powered by Razorpay & UPI' : 'Razorpay & UPI மூலம் பாதுகாப்பான கட்டணம்'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;